import type { ExistingSite } from "@/types/VendorService.types";

/** Normalize API / form values to dropdown tokens (`new` / `existing`). */
export function normalizeNewOrExistingSiteValue(raw: unknown): string {
  if (raw == null) return "";
  const trimmed = String(raw).trim();
  if (trimmed === "") return "";
  const lower = trimmed.toLowerCase();
  if (lower === "new") return "new";
  if (lower === "existing") return "existing";
  return trimmed;
}

/**
 * Read `newOrExistingSite` from a site/facility API item.
 * Supports top-level field or nested `siteStatuses` object (catalogue rows).
 */
export function resolveNewOrExistingSiteFromApi(item: unknown): string {
  if (item == null || typeof item !== "object") return "";
  const s = item as Record<string, unknown>;
  const nested =
    s.siteStatuses != null &&
    typeof s.siteStatuses === "object" &&
    !Array.isArray(s.siteStatuses)
      ? (s.siteStatuses as Record<string, unknown>)
      : undefined;
  const raw =
    nested?.newOrExistingSite ??
    s.newOrExistingSite ??
    s.isThisNewOrExistingSite;
  return normalizeNewOrExistingSiteValue(raw);
}

/** True when the API row has its own mode key (`newOrExistingSite` / `isThisNewOrExistingSite`), even if the value is null/empty. */
function rowHasModeField(item: unknown): boolean {
  if (item == null || typeof item !== "object") return false;
  return "isThisNewOrExistingSite" in item || "newOrExistingSite" in item;
}

/**
 * Read top-level `isThisNewOrExistingSite` from a site/facility API item.
 * Independent of `newOrExistingSite` / `siteStatuses` (those may differ).
 */
export function resolveIsThisNewOrExistingSiteFromApi(item: unknown): string {
  if (item == null || typeof item !== "object") return "";
  const s = item as Record<string, unknown>;
  return normalizeNewOrExistingSiteValue(s.isThisNewOrExistingSite);
}

/** Read `isConfirmed` from top-level or nested `siteStatuses`. */
export function resolveIsConfirmedFromApi(item: unknown): boolean | null {
  if (item == null || typeof item !== "object") return null;
  const s = item as Record<string, unknown>;
  const nested =
    s.siteStatuses != null &&
    typeof s.siteStatuses === "object" &&
    !Array.isArray(s.siteStatuses)
      ? (s.siteStatuses as Record<string, unknown>)
      : undefined;
  const raw = nested?.isConfirmed ?? s.isConfirmed;
  if (raw === true || raw === false) return raw;
  return null;
}

/**
 * Build facilityId → newOrExistingSite from assessment GET `siteStatuses[]`.
 * Primary join: siteStatuses[].manufacturingFacilityInformation.id
 * Fallback: siteStatuses[].facilityId or siteStatuses[].id when it matches a facility row id.
 */
export function buildNewOrExistingSiteByFacilityIdMap(
  siteStatuses: unknown
): Map<string, string> {
  const map = new Map<string, string>();
  if (!Array.isArray(siteStatuses)) return map;

  for (const entry of siteStatuses) {
    if (entry == null || typeof entry !== "object") continue;
    const o = entry as Record<string, unknown>;
    const mode = normalizeNewOrExistingSiteValue(
      o.newOrExistingSite ?? o.isThisNewOrExistingSite
    );
    if (!mode) continue;

    const mfi = o.manufacturingFacilityInformation;
    const facilityIdFromMfi =
      mfi != null && typeof mfi === "object"
        ? (mfi as Record<string, unknown>).id
        : undefined;
    if (facilityIdFromMfi != null && String(facilityIdFromMfi).trim() !== "") {
      map.set(String(facilityIdFromMfi), mode);
      continue;
    }

    const facilityId = o.facilityId ?? o.id;
    if (facilityId != null && String(facilityId).trim() !== "") {
      map.set(String(facilityId), mode);
    }
  }

  return map;
}

/** Join assessment `siteStatuses[]` onto `vendorManufacturingFacilities[]` rows. */
export function attachNewOrExistingSiteToManufacturingFacilities<
  T extends Record<string, unknown>,
>(facilities: T[], siteStatuses: unknown): (T & { newOrExistingSite: string })[] {
  const modeByFacilityId = buildNewOrExistingSiteByFacilityIdMap(siteStatuses);
  return facilities.map((facility) => {
    const fid =
      facility.id != null && String(facility.id).trim() !== ""
        ? String(facility.id)
        : "";
    const fromMap = fid ? modeByFacilityId.get(fid) : undefined;
    const fromRow = resolveNewOrExistingSiteFromApi(facility);
    return {
      ...facility,
      newOrExistingSite:
        rowHasModeField(facility) && fromRow === "" ? "" : fromMap ?? fromRow ?? "",
    };
  });
}

const strFromApi = (v: unknown): string =>
  typeof v === "string" ? v : v != null ? String(v) : "";

/** Map one manufacturing facility row from assessment GET (address fields + joined mode). */
export function mapAssessmentManufacturingFacilityRow(
  item: Record<string, unknown>,
  siteStatuses: unknown
): Record<string, unknown> {
  const fid = item.id != null ? String(item.id) : "";
  const modeByFacilityId = buildNewOrExistingSiteByFacilityIdMap(siteStatuses);
  const fromRow = resolveNewOrExistingSiteFromApi(item);
  // The row carries its own mode field but it is empty (user cleared it): keep it empty,
  // don't fall back to a mode from siteStatuses.
  const newOrExistingSite = rowHasModeField(item) && fromRow === ""
    ? ""
    : (fid ? modeByFacilityId.get(fid) : undefined) ?? fromRow ?? "";

  return {
    ...item,
    manufacturerAddress: strFromApi(item.manufacturerAddress ?? item.address),
    manufacturerCity: strFromApi(item.manufacturerCity ?? item.city),
    manufacturerState: strFromApi(item.manufacturerState ?? item.state),
    manufacturerZip: strFromApi(item.manufacturerZip ?? item.zip),
    manufacturerCountry: strFromApi(item.manufacturerCountry ?? item.country),
    newOrExistingSite,
  };
}

/** Map `vendorManufacturingFacilities` + root `siteStatuses` from assessment GET responses. */
export function mapManufacturingFacilitiesFromAssessmentResponse(
  responseData: Record<string, unknown> | null | undefined
): Record<string, unknown>[] {
  const mf = responseData?.vendorManufacturingFacilities;
  const arr = Array.isArray(mf) ? mf : [];
  const siteStatuses = responseData?.siteStatuses;
  return arr.map((item) =>
    mapAssessmentManufacturingFacilityRow(
      item as Record<string, unknown>,
      siteStatuses
    )
  );
}

/** Normalize GET /vendor/getExistingSites (and guest equivalent) into ExistingSite for UI. */
export function normalizeExistingSiteFromApi(raw: unknown): ExistingSite | null {
  if (raw == null || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = o.id ?? o.siteId ?? o.manufacturingFacilityId;
  if (id == null || String(id).trim() === "") return null;

  const siteStatuses =
    o.siteStatuses != null && typeof o.siteStatuses === "object"
      ? (o.siteStatuses as ExistingSite["siteStatuses"])
      : undefined;

  const businessCategoryRaw = o.businessCategory ?? o.businessCategories;
  const siteStatusRaw = o.siteStatus;
  const siteStatus =
    siteStatusRaw != null &&
    typeof siteStatusRaw === "object" &&
    !Array.isArray(siteStatusRaw)
      ? (siteStatusRaw as Record<string, unknown>).id
      : siteStatusRaw;
  const businessCategory =
    businessCategoryRaw != null &&
    typeof businessCategoryRaw === "object" &&
    !Array.isArray(businessCategoryRaw)
      ? (businessCategoryRaw as Record<string, unknown>).id
      : businessCategoryRaw;

  return {
    id: String(id),
    name: String(o.name ?? o.siteName ?? o.facilityName ?? ""),
    contact: String(o.contact ?? ""),
    emailId: String(o.emailId ?? o.email ?? ""),
    phone: String(o.phone ?? ""),
    address: String(o.address ?? o.manufacturerAddress ?? ""),
    city: String(o.city ?? o.manufacturerCity ?? ""),
    state: String(o.state ?? o.manufacturerState ?? ""),
    zip: String(o.zip ?? o.manufacturerZip ?? ""),
    country:
      o.country != null || o.manufacturerCountry != null
        ? String(o.country ?? o.manufacturerCountry)
        : null,
    cityStateZipCountry: (o.cityStateZipCountry as string | null | undefined) ?? null,
    newOrExistingSite: resolveNewOrExistingSiteFromApi(o),
    orbcSiteCode:
      o.orbcSiteCode != null ? String(o.orbcSiteCode) : null,
    productPortalSiteId:
      o.productPortalSiteId != null ? String(o.productPortalSiteId) : null,
    siteType: (o.siteType as ExistingSite["siteType"]) ?? null,
    businessCategory:
      businessCategory != null && String(businessCategory).trim() !== ""
        ? String(businessCategory)
        : null,
    siteStatus:
      siteStatus != null && String(siteStatus).trim() !== ""
        ? String(siteStatus)
        : null,
    gfsiCertified: o.gfsiCertified != null ? String(o.gfsiCertified) : null,
    organicCertified:
      o.organicCertified != null ? String(o.organicCertified) : null,
    skuNumber: o.skuNumber != null ? String(o.skuNumber) : null,
    sku: o.sku != null ? String(o.sku) : null,
    remarks: o.remarks != null ? String(o.remarks) : null,
    siteStatuses,
  };
}

export function normalizeExistingSitesFromApi(data: unknown): ExistingSite[] {
  const arr = Array.isArray(data)
    ? data
    : data != null &&
        typeof data === "object" &&
        Array.isArray((data as { data?: unknown }).data)
      ? (data as { data: unknown[] }).data
      : [];
  const out: ExistingSite[] = [];
  for (const item of arr) {
    const normalized = normalizeExistingSiteFromApi(item);
    if (normalized) out.push(normalized);
  }
  return out;
}

/** One form row used to compute which catalogue sites are already claimed. */
export type SiteCatalogReservationRow = {
  newOrExistingSite?: string | null;
  existingSiteId?: string | number | null;
  /** Manufacturing `name` or vendor `siteName`. */
  siteName?: string | null;
  /** Persisted row id when it matches a catalogue site id. */
  rowId?: string | number | null;
};

const normalizeCatalogSiteName = (raw: string): string => raw.trim().toLowerCase();

/** First catalogue id per normalized display name. */
function buildCatalogNameToIdMap(catalog: ExistingSite[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const s of catalog) {
    if (s == null) continue;
    const label =
      s.name != null ? normalizeCatalogSiteName(String(s.name)) : "";
    if (label !== "" && !map.has(label)) {
      map.set(label, String(s.id));
    }
  }
  return map;
}

/**
 * Catalogue site ids claimed by form rows.
 * When `excludeRowIndex` is set, that row is omitted (for per-row dropdown options).
 * For lock/disable logic, omit `excludeRowIndex` so every row is counted.
 */
function buildReservedCatalogSiteIds(
  catalog: ExistingSite[],
  rows: SiteCatalogReservationRow[],
  options?: { excludeRowIndex?: number }
): Set<string> {
  const reserved = new Set<string>();
  if (!Array.isArray(catalog) || catalog.length === 0 || !Array.isArray(rows)) {
    return reserved;
  }

  const catalogIds = new Set(catalog.map((s) => String(s.id)));
  const nameToCatalogId = buildCatalogNameToIdMap(catalog);

  rows.forEach((row, index) => {
    if (options?.excludeRowIndex === index) return;

    const extId = row.existingSiteId;
    if (extId != null && String(extId).trim() !== "") {
      reserved.add(String(extId));
      return;
    }

    const rowName =
      row.siteName != null ? normalizeCatalogSiteName(String(row.siteName)) : "";
    if (rowName !== "" && nameToCatalogId.has(rowName)) {
      reserved.add(String(nameToCatalogId.get(rowName)));
      return;
    }

    const modeExisting =
      normalizeNewOrExistingSiteValue(row.newOrExistingSite) === "existing";
    if (modeExisting) {
      const rowId = row.rowId;
      if (
        rowId != null &&
        String(rowId).trim() !== "" &&
        catalogIds.has(String(rowId))
      ) {
        reserved.add(String(rowId));
      }
    }
  });

  return reserved;
}

/** How many catalogue sites are not claimed by any row in `rows`. */
export function countUnreservedCatalogSites(
  catalog: ExistingSite[],
  rows: SiteCatalogReservationRow[],
  options?: { excludeRowIndex?: number }
): number {
  if (!Array.isArray(catalog) || catalog.length === 0) return 0;
  const reserved = buildReservedCatalogSiteIds(catalog, rows, options);
  let n = 0;
  for (const s of catalog) {
    if (s != null && !reserved.has(String(s.id))) {
      n++;
    }
  }
  return n;
}

/**
 * Global unreserved catalogue count for New/Existing dropdown lock (all rows).
 * Maps each form row via `mapFormToReservationRow`, then delegates to `countUnreservedCatalogSites`.
 */
export function getGlobalUnreservedCatalogSiteCount<T>(
  catalog: ExistingSite[] | null | undefined,
  forms: T[] | null | undefined,
  mapFormToReservationRow: (form: T) => SiteCatalogReservationRow
): number {
  if (!Array.isArray(catalog) || catalog.length === 0) {
    return 0;
  }
  const rows = (forms ?? []).map(mapFormToReservationRow);
  return countUnreservedCatalogSites(catalog, rows);
}
